import {
  PresentationDisplayInterface,
  SlideInterface,
} from 'interfaces/interfaces-data';
import { create } from 'zustand';

// Define the store interface
interface StudioEditStore {
  docsLoaded: PresentationDisplayInterface;
  setDocsLoaded: (
    value:
      | PresentationDisplayInterface
      | ((prev: PresentationDisplayInterface) => PresentationDisplayInterface)
  ) => void;

  // ActiveDocumentId state
  activeDocumentId: string;
  setActiveDocumentId: (value: string | ((prev: string) => string)) => void;

  // Additional convenience methods
  addSlides: (key: string, slides: SlideInterface[]) => void;
  updateSlides: (key: string, slides: SlideInterface[]) => void;
  removeSlides: (key: string) => void;
  updateSlide: (
    key: string,
    slideId: string,
    updates: Partial<SlideInterface>
  ) => void;
  clearAll: () => void;
}

// Create the Zustand store
export const useStudioEditStore = create<StudioEditStore>((set, get) => ({
  // Initial state - empty object like your useState
  docsLoaded: {},

  // Initial state - empty string like your useState
  activeDocumentId: '',

  // Main setter function - works exactly like useState setter
  setDocsLoaded: (value) => {
    set((state) => ({
      docsLoaded: typeof value === 'function' ? value(state.docsLoaded) : value,
    }));
  },

  // ActiveDocumentId setter - works exactly like useState setter
  setActiveDocumentId: (value) => {
    set((state) => ({
      activeDocumentId:
        typeof value === 'function' ? value(state.activeDocumentId) : value,
    }));
  },

  // Convenience method to add slides for a specific key
  addSlides: (key: string, slides: SlideInterface[]) => {
    set((state) => ({
      docsLoaded: {
        ...state.docsLoaded,
        [key]: slides,
      },
    }));
  },

  // Convenience method to update slides for a specific key
  updateSlides: (key: string, slides: SlideInterface[]) => {
    set((state) => ({
      docsLoaded: {
        ...state.docsLoaded,
        [key]: slides,
      },
    }));
  },

  // Convenience method to remove slides for a specific key
  removeSlides: (key: string) => {
    set((state) => {
      const newDocsLoaded = { ...state.docsLoaded };
      delete newDocsLoaded[key];
      return { docsLoaded: newDocsLoaded };
    });
  },

  // Convenience method to update a specific slide
  updateSlide: (
    key: string,
    slideId: string,
    updates: Partial<SlideInterface>
  ) => {
    set((state) => {
      const slides = state.docsLoaded[key];
      if (!slides) return state;

      const updatedSlides = slides.map((slide) =>
        slide.id === slideId ? { ...slide, ...updates } : slide
      );

      return {
        docsLoaded: {
          ...state.docsLoaded,
          [key]: updatedSlides,
        },
      };
    });
  },

  // Convenience method to clear all docs
  clearAll: () => {
    set({ docsLoaded: {} });
  },
}));
